import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing');
  const [errorMsg, setErrorMsg] = useState('');

  const extractSessionId = () => {
    // Method 1: Hash fragment (standard)
    const hash = window.location.hash || location.hash || '';
    const hashParams = new URLSearchParams(hash.substring(1));
    let sessionId = hashParams.get('session_id');
    if (sessionId) return sessionId;

    // Method 2: Query params (some mobile browsers)
    const searchParams = new URLSearchParams(window.location.search);
    sessionId = searchParams.get('session_id');
    if (sessionId) return sessionId;

    // Method 3: Regex parse full URL
    const fullUrl = window.location.href;
    const sessionMatch = fullUrl.match(/session_id=([^&# ]+)/);
    if (sessionMatch) return decodeURIComponent(sessionMatch[1]);

    return null;
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const sessionId = extractSessionId();

      if (!sessionId) {
        // No session_id — check if already logged in
        try {
          const resp = await axios.get(`${API}/auth/me`, { withCredentials: true, timeout: 5000 });
          if (resp.status === 200) {
            setUser(resp.data);
            navigate('/dashboard', { replace: true });
            return;
          }
        } catch {}
        setErrorMsg('No login session found. Please try signing in again.');
        setStatus('failed');
        return;
      }

      // Attempt session exchange — retry with delay on failure
      try {
        console.log('[AUTH] Exchanging session_id, length:', sessionId?.length);
        let response;
        const maxAttempts = 2;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          try {
            if (attempt > 0) {
              console.log(`[AUTH] Retry attempt ${attempt + 1}/${maxAttempts}, waiting 3s...`);
              await new Promise(r => setTimeout(r, 3000));
            }
            response = await axios.post(
              `${API}/auth/session`,
              { session_id: sessionId },
              { withCredentials: true, timeout: 30000 }
            );
            break; // Success
          } catch (err) {
            if (attempt === maxAttempts - 1) throw err;
            console.warn(`[AUTH] Attempt ${attempt + 1} failed:`, err?.response?.status);
          }
        }
        console.log('[AUTH] Exchange success, got user:', response.data?.email);

        // Store token in localStorage (backup for when cookies fail)
        if (response.data.session_token) {
          localStorage.setItem('session_token', response.data.session_token);
        }

        setUser(response.data);

        // Clean URL
        if (window.location.hash) {
          window.history.replaceState(null, '', window.location.pathname);
        }

        navigate('/dashboard', { state: { user: response.data }, replace: true });
      } catch (error) {
        const rawError = error?.response?.data?.detail || error?.message || '';
        const statusCode = error?.response?.status;
        console.error('[AUTH] Exchange failed:', statusCode, rawError, 'session_id length:', sessionId?.length);
        
        let friendlyMsg = 'Connection failed. Please try again.';
        if (statusCode === 504) {
          friendlyMsg = 'Server timed out. Please wait a moment and try again.';
        } else if (statusCode === 502) {
          friendlyMsg = 'Authentication service error. Please try again in a moment.';
        } else if (rawError.includes('Network') || rawError.includes('network')) {
          friendlyMsg = 'Cannot reach the server. Please check your connection and try again.';
        } else if (rawError.includes('Balance') || rawError.includes('balance')) {
          friendlyMsg = rawError;
        } else if (rawError) {
          friendlyMsg = `Login failed: ${rawError}`;
        }
        
        setErrorMsg(friendlyMsg);
        setStatus('failed');
      }
    };

    processSession();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoginAgain = () => {
    // Clear any stale state and redirect to fresh Google login
    localStorage.removeItem('session_token');
    hasProcessed.current = false;
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="text-center max-w-sm">
        {status === 'failed' ? (
          <>
            <div className="w-16 h-16 rounded-full bg-red-500/10 border-2 border-red-500/30 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </div>
            <p className="text-slate-200 text-lg font-semibold mb-2">Login Failed</p>
            <p className="text-slate-400 text-sm mb-6">{errorMsg}</p>
            <button
              onClick={handleLoginAgain}
              className="w-full py-3 px-4 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-xl text-blue-300 font-semibold transition-all"
              data-testid="auth-retry-btn"
            >
              Back to Login
            </button>
          </>
        ) : (
          <>
            <div className="w-16 h-16 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-300 text-lg">Authenticating...</p>
          </>
        )}
      </div>
    </div>
  );
}
